import React from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormGroup, Select, showSnackbar } from "@cofano/cck";
import { intl } from "LocaleWrapper";
import { FormDialog } from "components/dialogs/Dialogs";
import { FormErrorMessage } from "components/form/FormErrorMessage";
import {
	WidgetSaveDefaultSchemaInput,
	WidgetSaveDefaultSchemaOutput,
	widgetSaveDefaultSchema,
} from "view/dashboard/utils/dashboard.schema";
import { WidgetTemplate } from "view/dashboard/types/widget";
import { Permission } from "__generated__/enums";
import { SaveDashboardWidgetsDocument } from "__generated__/view/dashboard/graphql/dashboard.mutations.generated";

const FORM_ID = "dashboardDefaultForm";

type WidgetSaveDefaultDialogProps = {
	open: boolean;
	onClose: () => void;
	widgets: Array<WidgetTemplate>;
};

const WidgetSaveDefaultDialog = ({ open, onClose, widgets }: WidgetSaveDefaultDialogProps) => {

	const { control, handleSubmit, reset } = useForm<WidgetSaveDefaultSchemaInput, unknown, WidgetSaveDefaultSchemaOutput>({
		resolver: zodResolver(widgetSaveDefaultSchema),
		defaultValues: { permission: null },
	});

	const [ saveDashboardWidgets, { loading } ] = useMutation(SaveDashboardWidgetsDocument);

	const handleOnClose = () => {
		onClose();
		reset({ permission: null });
	};

	const save = (data: WidgetSaveDefaultSchemaOutput) => {
		saveDashboardWidgets({
			variables: {
				permission: data.permission,
				widgets: widgets,
			},
		}).then(() => {
			showSnackbar(intl.formatMessage({ id: "SUCCESSFUL_SAVED" }), { variant: "success" });
			handleOnClose();
		});
	};

	return (
		<FormDialog
			open={open}
			onClose={handleOnClose}
			title={intl.formatMessage({ id: "WIDGET_SAVE_FOR_ORGANIZATION" })}
			form={FORM_ID}
			maxWidth="sm"
			loading={loading}>
			<form id={FORM_ID} onSubmit={event => void handleSubmit(save)(event)}>
				<FormGroup label={intl.formatMessage({ id: "USER_TYPE" })} labelPlacement="left-center" required>
					<Controller
						name="permission"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<Select
									{...field}
									fetchOptions={Object.values(Permission)}
									getOptionLabel={option => intl.formatMessage({ id: `USER_TYPE_${option}` })}
									disableSearch />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			</form>
		</FormDialog>
	);
};

export default WidgetSaveDefaultDialog;
